/**
 * 考試規劃表畫面。
 * Source of truth: docs/spec/EXAM_PLANNER_SPEC.md §1、§4(P2)。
 *
 * 使用者填期中考/期末考/小考日期 → 即時看到每次考試的複習日期範圍。
 * 視覺 = 學術風格設計系統(pageShell + academicButton;見 wiki page-470f59c0)。
 * 專業介面鐵則:無音效/mascot/彩光/戲謔文案。純學習側,不碰戰鬥數值。
 */
import { pageShell, academicButton } from '../ui/components';
import type { ExamEvent, ExamType, ExamWindow, Subject } from './types';
import { EXAM_TYPE_LABEL, SUBJECT_LABEL, EXAM_SUBJECTS } from './types';
import { loadExamPlan, addExamEvent, removeExamEvent } from './store';
import { computeWindows } from './scope';

const TYPES: ExamType[] = ['quiz', 'midterm', 'final'];

/** 'YYYY-MM-DD' → '3/15'(顯示用簡短日期);null → '開學'。 */
function shortDate(d: string | null): string {
  if (d === null) return '開學';
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  return `${Number(m[1])}/${Number(m[2])}`;
}

function displayName(e: ExamEvent): string {
  if (e.name?.trim()) return e.name.trim();
  // 小考帶科目 → 「英文小考」;段考 / 無科目 → 預設名。
  if (e.type === 'quiz' && e.subject) return `${SUBJECT_LABEL[e.subject]}小考`;
  return EXAM_TYPE_LABEL[e.type];
}

/**
 * Render 考試規劃表。`onBack` 給返回鈕;`onReviewExam`(選填)讓使用者點某次考試
 * 直接去複習該範圍(P4 接上後傳入)。
 */
export function runExamPlanner(
  container: HTMLElement,
  opts: { onBack: () => void; onReviewExam?: (window: ExamWindow) => void } = { onBack: () => {} },
): void {
  container.innerHTML = '';
  const shell = pageShell(container, {
    title: '考試規劃',
    subtitle: '填上期中考、期末考、小考的日期。之後上傳的考卷與作業會依日期自動歸到對應的那次考試,複習時直接選哪次考就好。',
    onBack: opts.onBack,
  });

  const root = document.createElement('div');
  root.style.cssText = 'display:flex;flex-direction:column;gap:20px;';
  shell.body.appendChild(root);

  // ── 新增表單 ──────────────────────────────────────────────
  const form = document.createElement('div');
  form.style.cssText =
    'display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;padding:16px 18px;' +
    'background:var(--ef-paper-bright);border:1px solid var(--ef-line);border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);';
  root.appendChild(form);

  const fieldLabel = (text: string): HTMLElement => {
    const l = document.createElement('label');
    l.textContent = text;
    l.style.cssText = 'display:flex;flex-direction:column;gap:4px;font-size:13px;color:var(--ef-sub);font-weight:600;';
    return l;
  };
  const inputCss =
    'padding:8px 10px;font-size:15px;font-family:inherit;color:var(--ef-ink);' +
    'background:var(--ef-paper-bright);border:1px solid var(--ef-line);border-radius:8px;';

  const typeWrap = fieldLabel('考試類型');
  const typeSel = document.createElement('select');
  typeSel.style.cssText = inputCss;
  for (const t of TYPES) {
    const o = document.createElement('option');
    o.value = t;
    o.textContent = EXAM_TYPE_LABEL[t];
    typeSel.appendChild(o);
  }
  typeWrap.appendChild(typeSel);

  // 科目(只有小考要選;段考全科)。隨 type 切換顯示。
  const subjWrap = fieldLabel('科目');
  subjWrap.classList.add('exam-plan-subject');
  const subjSel = document.createElement('select');
  subjSel.style.cssText = inputCss;
  for (const s of EXAM_SUBJECTS) {
    const o = document.createElement('option');
    o.value = s;
    o.textContent = SUBJECT_LABEL[s];
    subjSel.appendChild(o);
  }
  subjSel.value = 'english';
  subjWrap.appendChild(subjSel);

  const syncSubjectVisibility = (): void => {
    subjWrap.style.display = typeSel.value === 'quiz' ? '' : 'none';
  };
  typeSel.addEventListener('change', syncSubjectVisibility);
  syncSubjectVisibility();

  const dateWrap = fieldLabel('日期');
  const dateInput = document.createElement('input');
  dateInput.type = 'date';
  dateInput.style.cssText = inputCss;
  dateWrap.appendChild(dateInput);

  const nameWrap = fieldLabel('名稱(選填)');
  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.placeholder = '例:第二次小考';
  nameInput.style.cssText = inputCss + 'min-width:140px;';
  nameWrap.appendChild(nameInput);

  const addBtn = academicButton({
    label: '＋ 新增',
    variant: 'primary',
    onClick: () => {
      const date = dateInput.value;
      if (!date) {
        dateInput.focus();
        return;
      }
      const type = typeSel.value as ExamType;
      addExamEvent({
        name: nameInput.value,
        type,
        date,
        subject: type === 'quiz' ? (subjSel.value as Subject) : undefined,
      });
      nameInput.value = '';
      dateInput.value = '';
      renderList();
    },
  });

  form.append(typeWrap, subjWrap, dateWrap, nameWrap, addBtn);

  // ── 清單 ──────────────────────────────────────────────────
  const list = document.createElement('div');
  list.style.cssText = 'display:flex;flex-direction:column;gap:10px;';
  root.appendChild(list);

  const empty = document.createElement('p');
  empty.textContent = '還沒有任何考試。先把期中考、期末考的日期填進去吧。';
  empty.style.cssText = 'color:var(--ef-sub);font-size:15px;line-height:1.7;padding:8px 2px;';

  const typeBadge = (type: ExamType): string => {
    const seg = type === 'quiz' ? 'var(--ef-sub)' : 'var(--ef-green)';
    return `display:inline-block;padding:2px 10px;border-radius:999px;font-size:12px;font-weight:700;color:#fff;background:${seg};`;
  };

  function renderList(): void {
    list.innerHTML = '';
    const windows = computeWindows(loadExamPlan());
    if (windows.length === 0) {
      list.appendChild(empty);
      return;
    }
    for (const w of windows) {
      const row = document.createElement('div');
      row.style.cssText =
        'display:flex;align-items:center;gap:12px;padding:14px 16px;background:var(--ef-paper-bright);' +
        'border:1px solid var(--ef-line);border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);';

      const badge = document.createElement('span');
      badge.style.cssText = typeBadge(w.event.type);
      badge.textContent = EXAM_TYPE_LABEL[w.event.type];

      const mid = document.createElement('div');
      mid.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:3px;min-width:0;';
      const title = document.createElement('div');
      title.style.cssText = 'font-family:var(--ef-serif);font-weight:700;font-size:17px;color:var(--ef-ink);';
      title.textContent = `${displayName(w.event)} · ${shortDate(w.event.date)}`;
      const range = document.createElement('div');
      range.style.cssText = 'font-size:13px;color:var(--ef-sub);';
      range.textContent = `複習範圍:${shortDate(w.from)} → ${shortDate(w.to)}`;
      mid.append(title, range);

      const actions = document.createElement('div');
      actions.style.cssText = 'display:flex;gap:8px;';
      if (opts.onReviewExam) {
        actions.appendChild(
          academicButton({ label: '複習這次', variant: 'secondary', onClick: () => opts.onReviewExam?.(w) }),
        );
      }
      actions.appendChild(
        academicButton({
          label: '刪除',
          variant: 'danger',
          onClick: () => {
            removeExamEvent(w.event.id);
            renderList();
          },
        }),
      );

      row.append(badge, mid, actions);
      list.appendChild(row);
    }
  }

  renderList();
}
